/**
 * Single source of truth for Synesthesia Identifier Step 2 and Step 3 options.
 * Terminology and descriptions are humanized and avoid long dashes.
 * Slugs are stable for URLs and result pairing.
 */

/** Triggers (inducers): what sets off the synesthetic experience. 14 options. */
export const TRIGGERS = [
  { slug: 'sight', label: 'Sight', description: 'What you see: colours, shapes, or things in front of you' },
  { slug: 'sound', label: 'Sound', description: 'Music, voices, or any sound' },
  { slug: 'touch', label: 'Touch', description: 'Physical contact or something touching your skin' },
  { slug: 'taste', label: 'Taste', description: 'Something you taste or a flavour' },
  { slug: 'smell', label: 'Smell', description: 'A scent or odour' },
  { slug: 'sequences', label: 'Sequences', description: 'Ordered things like letters, numbers, days, months, or any sequence' },
  { slug: 'words', label: 'Words', description: 'Words, letters, or language (written or spoken)' },
  { slug: 'pain', label: 'Pain', description: 'Pain or physical discomfort' },
  { slug: 'emotions', label: 'Emotions', description: 'How you feel: joy, sadness, anger, or other emotions' },
  { slug: 'sex-romance', label: 'Sex or romance', description: 'Sexual or romantic feelings or experiences' },
  { slug: 'people', label: 'People', description: 'A person, a face, or someone\'s presence' },
  { slug: 'movement', label: 'Movement', description: 'Motion or movement, yours or something you see' },
  { slug: 'ideas', label: 'Ideas and thinking', description: 'A thought, an idea, or the act of thinking' },
  { slug: 'mathematics', label: 'Mathematics', description: 'Numbers, sums, or maths in general' }
];

/** Experiences (concurrents): what the person experiences when triggered. 10 options. */
export const EXPERIENCES = [
  { slug: 'visual', label: 'Visual', description: 'You see something: colours, shapes, or images' },
  { slug: 'visual-minds-eye', label: 'Visual (in your mind only)', description: 'You see it in your mind\'s eye, not out in the world' },
  { slug: 'visual-feel', label: 'Visual (you feel it, don\'t see it)', description: 'You sense a colour or form without actually seeing it' },
  { slug: 'spatial', label: 'Spatial', description: 'Where things sit: position, layout, or a map in space' },
  { slug: 'textures', label: 'Textures', description: 'How something feels: smooth, rough, or other textures' },
  { slug: 'sounds', label: 'Sounds', description: 'You hear or sense a sound or music' },
  { slug: 'tastes-smells', label: 'Tastes or smells', description: 'A flavour or a scent' },
  { slug: 'tactile', label: 'Tactile', description: 'A sensation on your skin or in your body' },
  { slug: 'personification', label: 'Personification', description: 'A personality, gender, or character (e.g. letters or numbers feel like people)' },
  { slug: 'something-different', label: 'Something else', description: 'None of these fit. We\'ll point you to more options.' }
];

/** Get trigger label by slug (for Step 3 headings and SEO). */
export const getTriggerLabel = (slug) => TRIGGERS.find((t) => t.slug === slug)?.label ?? slug;

/** Get experience label by slug (for Step 3 headings and SEO). */
export const getExperienceLabel = (slug) => EXPERIENCES.find((e) => e.slug === slug)?.label ?? slug;
